import { useCallback } from 'react';
import validateEmail from '../../Validation/Email';
import validatePassword from '../../Validation/Password';
import { validatePhoneNumber } from '../../Validation/Phone';
import validateName from '../../Validation/Name';
import validateSelect from '../../Validation/Select';
import validateDate from '../../Validation/Date';
import validateSelects from '../../Validation/Selects';
import {
  ENV_VERIFIED_TRAINER,
  isBrandActive,
} from '../../config/environment.config';
import { FormState, FormErrors } from './useFormState';

export default function useFormValidation() { 
  const validateRegisterForm = useCallback(
    (form: FormState): FormErrors => {
      const errors: FormErrors = {};

      // Validation email
      const emailValidation = validateEmail(form.email);
      if (!emailValidation.isValid) {
        errors.email = emailValidation.message;
      }

      // Validation password
      const passwordValidation = validatePassword(
        form.password,
        form.cpassword
      );
      if (!passwordValidation.isValid) {
        errors.password = passwordValidation.message;
      }

      // Validation phone
      const phoneError = validatePhoneNumber(form.phone);
      if (phoneError) {
        errors.phone = phoneError;
      }

      // Validation terms
      if (!form.terms) {
        errors.terms = 'Veuillez accepter la politique des données';
      }

      // Validation CGU/CGV
      if (!form.ccg) {
        errors.ccg = 'Veuillez accepter les CGUs et CGVs';
      }

      return errors;
    },
    []
  );

  const validateRegisterFirstForm = useCallback(
    async (
      form: FormState,
      userProvider?: string
    ): Promise<{ errors: FormErrors; isValid: boolean }> => {
      const errors: FormErrors = {};
      let isValid = true;
      // Validation email/phone selon le provider
      if (userProvider === 'phone' && !validateEmail(form.email).isValid) {
        errors.email = validateEmail(form.email).message;
        isValid = false;
      }

      if (userProvider === 'google.com' && validatePhoneNumber(form.phone)) {
        errors.phone = validatePhoneNumber(form.phone);
        isValid = false;
      }

      // Détermination des champs à vérifier selon la marque
      const fieldArray = isBrandActive(ENV_VERIFIED_TRAINER) || form.option === 2
        ? ['firstName', 'lastName', 'gender', 'birthday', 'sponsorship']
        : ['firstName', 'lastName', 'gender', 'birthday', 'courses'];

      // Validation des champs communs
      fieldArray.forEach((field) => {
        const validateFn =
          field === 'birthday'
            ? validateDate
            : field === 'courses'
            ? validateSelects
            : field === 'gender'
            ? validateSelect
            : validateName;

        const fieldValue = (() => {
          switch (field) {
            case 'courses':
              return form.courses;
            case 'birthday':
              return form.birthday;
            case 'firstName':
              return form.firstName;
            case 'lastName':
              return form.lastName;
            case 'gender':
              return form.gender;
            case 'sponsorship':
              return form.sponsorship;
            default:
              return '';
          }
        })();

        const { isValid: fieldValid, message } = validateFn(fieldValue);
        
        if (!fieldValid) {
          errors[field as keyof FormErrors] = message;
          isValid = false;
        }
      });
      return { errors, isValid };
    },
    []
  );

  const validateField = useCallback(
    (field: keyof FormState, value: any, form?: FormState): string => {
      switch (field) {
        case 'email':
          return validateEmail(value).message;
        case 'phone':
          return validatePhoneNumber(value);
        case 'password':
          // Pour password, on valide avec la confirmation si elle existe
          if (form?.cpassword) {
            return validatePassword(value, form.cpassword).message;
          }
          // Si pas de confirmation, on valide juste que le champ n'est pas vide
          if (!value || value.trim() === '') {
            return 'Veuillez remplir ce champ';
          }
          return '';
        case 'cpassword':
          // Pour cpassword, on valide avec le password
          if (form?.password) {
            return validatePassword(form.password, value).message;
          }
          // Si pas de password, on valide juste que le champ n'est pas vide
          if (!value || value.trim() === '') {
            return 'Veuillez confirmer votre mot de passe';
          }
          return '';
        case 'firstName':
        case 'lastName':
          return validateName(value).message;
        case 'gender':
          return validateSelect(value).message;
        case 'birthday':
          return validateDate(value).message;
        default:
          return '';
      }
    },
    []
  );

  return {
    validateRegisterForm,
    validateRegisterFirstForm,
    validateField,
  };
}




